import {
  ChevronDown,
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  RefreshCw,
  Sun,
} from "lucide-react";
import { useId, useState } from "react";
import type { LucideIcon } from "lucide-react";

import type {
  ForecastHour,
  GameForecast,
  ScheduleForecastState,
} from "../../domain/weather";
import { Button } from "@/shared/ui/button";

interface GameWeatherProps {
  state: ScheduleForecastState | undefined;
  onRetry: () => void;
  refreshing?: boolean;
}

export function GameWeather({
  state,
  onRetry,
  refreshing = false,
}: GameWeatherProps) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  if (!state) return null;

  if (state.status === "loading") {
    return (
      <div
        className="border-t border-border pt-2 text-xs text-foreground-muted"
        role="status"
      >
        Loading game forecast…
      </div>
    );
  }
  if (state.status === "out_of_range") {
    return (
      <div className="border-t border-border pt-2 text-xs text-foreground-muted">
        Forecast available within 16 days of game.
      </div>
    );
  }
  if (state.status === "unavailable") {
    return (
      <div className="border-t border-border pt-2 text-xs text-foreground-muted">
        Forecast unavailable for this game.
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="flex items-center justify-between gap-2 border-t border-border pt-2 text-xs text-foreground-muted">
        <span>Forecast temporarily unavailable.</span>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          onClick={onRetry}
          pending={refreshing}
          pendingLabel="REFRESHING…"
        >
          <RefreshCw aria-hidden="true" /> RETRY
        </Button>
      </div>
    );
  }

  const { forecast } = state;
  const WeatherIcon = iconForWeatherCode(forecast.weatherCode);
  return (
    <div className="border-t border-border pt-2">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-xs text-foreground-muted"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((value) => !value)}
      >
        <WeatherIcon
          className="size-4 shrink-0 text-primary"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          {forecast.rainoutRisk ? (
            <span
              className={`font-bold uppercase ${rainoutRiskClass(forecast.rainoutRisk)}`}
            >
              Rainout risk {forecast.rainoutRisk}
            </span>
          ) : null}
          <span className="font-bold text-foreground">
            {forecast.rainoutRisk ? " · " : ""}
            {forecast.condition}
          </span>
          {summaryDetails(forecast).map((detail) => (
            <span key={detail}> · {detail}</span>
          ))}
        </span>
        <ChevronDown
          className={`size-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {expanded ? (
        <div id={detailId} className="mt-2 border-t border-border/70 pt-2">
          <p className="m-0 text-xs font-bold text-foreground">
            {rainfallDetails(forecast).join(" · ")}
          </p>
          <p className="mt-0.5 mb-2 text-[0.68rem] text-foreground-muted">
            Weather-only estimate; field drainage and league decisions are not
            included.
          </p>
          <ol className="grid list-none gap-1.5 text-xs">
            {forecast.hours.map((hour) => (
              <ForecastHourRow key={hour.time} hour={hour} />
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function ForecastHourRow({ hour }: { hour: ForecastHour }) {
  const WeatherIcon = iconForWeatherCode(hour.weatherCode);
  const temperature = formatTemperature(hour.temperature, hour.temperature);
  const apparent = formatTemperature(
    hour.apparentTemperature,
    hour.apparentTemperature,
  );
  return (
    <li className="grid gap-1 rounded-[var(--radius-sm)] bg-surface/55 px-2 py-1.5 sm:grid-cols-[8rem_1fr] sm:items-center">
      <div className="flex items-center gap-1.5 font-bold text-foreground">
        <WeatherIcon className="size-3.5 text-primary" aria-hidden="true" />
        <span>
          {formatMinute(hour.periodStartMinute)}–
          {formatMinute(hour.periodEndMinute)}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-foreground-muted">
        <span>{hour.condition}</span>
        {temperature ? (
          <span>
            {temperature}
            {apparent && apparent !== temperature ? ` · feels ${apparent}` : ""}
          </span>
        ) : null}
        {hour.precipitationProbability !== null ? (
          <span>Precip {Math.round(hour.precipitationProbability)}%</span>
        ) : null}
        {hour.precipitation !== null && hour.precipitation >= 0.1 ? (
          <span>{hour.precipitation.toFixed(1)} mm</span>
        ) : null}
        {hour.windSpeed !== null ? (
          <span>Wind {Math.round(hour.windSpeed)} km/h</span>
        ) : null}
        {hour.windGusts !== null ? (
          <span>Gusts {Math.round(hour.windGusts)} km/h</span>
        ) : null}
      </div>
    </li>
  );
}

function summaryDetails(forecast: GameForecast): string[] {
  const details: string[] = [];
  const temperature = formatTemperature(
    forecast.temperatureMin,
    forecast.temperatureMax,
  );
  if (temperature) details.push(temperature);
  if (forecast.precipitationProbability !== null) {
    details.push(`Precip ${Math.round(forecast.precipitationProbability)}%`);
  }
  if (forecast.dayPrecipitation !== null) {
    details.push(`Day rain ${forecast.dayPrecipitation.toFixed(1)} mm`);
  }
  if (forecast.windGusts !== null) {
    details.push(`Gusts ${Math.round(forecast.windGusts)} km/h`);
  } else if (forecast.windSpeed !== null) {
    details.push(`Wind ${Math.round(forecast.windSpeed)} km/h`);
  }
  return details;
}

function rainfallDetails(forecast: GameForecast): string[] {
  const details: string[] = [];
  if (forecast.dayPrecipitation !== null) {
    details.push(`${forecast.dayPrecipitation.toFixed(1)} mm today`);
  }
  if (forecast.preGamePrecipitation !== null) {
    details.push(
      `${forecast.preGamePrecipitation.toFixed(1)} mm before first pitch`,
    );
  }
  if (forecast.precipitation !== null) {
    details.push(`${forecast.precipitation.toFixed(1)} mm during game`);
  }
  return details.length ? details : ["Rain accumulation unavailable"];
}

function rainoutRiskClass(risk: NonNullable<GameForecast["rainoutRisk"]>) {
  if (risk === "high") return "text-danger";
  if (risk === "moderate") return "text-warning";
  return "text-success";
}

function formatTemperature(
  minimum: number | null,
  maximum: number | null,
): string | null {
  if (minimum === null || maximum === null) return null;
  const low = Math.round(minimum);
  const high = Math.round(maximum);
  return low === high ? `${low}°C` : `${low}–${high}°C`;
}

function formatMinute(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour < 12 ? "AM" : "PM";
  const clockHour = hour % 12 || 12;
  return `${clockHour}${minute ? `:${String(minute).padStart(2, "0")}` : ""} ${period}`;
}

function iconForWeatherCode(code: number | null): LucideIcon {
  if (code === 0) return Sun;
  if (code === 1 || code === 2) return CloudSun;
  if (code === 3 || code === null) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if ([51, 53, 55, 56, 57].includes(code)) return CloudDrizzle;
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return CloudRain;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return CloudSnow;
  if ([95, 96, 99].includes(code)) return CloudLightning;
  return Cloud;
}
